import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { RefreshCw } from 'lucide-react';
import {
  getExpenses, getExpenseJob, getExpenseTransactions, syncExpenses,
} from '../api/client';
import type {
  ExpenseAmount, ExpenseFilters, ExpenseGroup, ExpenseJob, ExpenseReport, ExpenseTransactionPage,
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import { canViewExpenses, expenseMonthRange, expenseNumber, expenseToday } from '../lib/expenses';

const inputClass = 'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm min-w-0';
const tabs = ['overview', 'trends', 'suppliers', 'transactions'] as const;
type Tab = typeof tabs[number];

function useExpenseFormat() {
  const { i18n, t } = useTranslation();
  return (amount: ExpenseAmount | null | undefined) => {
    const number = expenseNumber(amount);
    return number == null ? t('expenses.unavailable') : new Intl.NumberFormat(i18n.language, {
      style: 'currency', currency: 'ILS', minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(number);
  };
}

function GroupTable({ rows, onSelect, grandTotal }: { rows: ExpenseGroup[]; onSelect: (id: string) => void; grandTotal?: ExpenseAmount }) {
  const { t } = useTranslation();
  const format = useExpenseFormat();
  const total = grandTotal == null ? rows.reduce((sum, row) => sum + Number(row.accounting_amount), 0) : Number(grandTotal);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-start text-sm">
        <thead className="bg-gray-50"><tr>
          {['name', 'accountingAmount', 'share', 'count', 'vat', 'gross', 'previous', 'change'].map((key) =>
            <th key={key} scope="col" className="p-3 text-start">{t(`expenses.${key}`)}</th>)}
        </tr></thead>
        <tbody>{rows.map((row) => (
          <tr key={row.id} className="border-t">
            <td className="p-3"><button className="text-primary-600 underline" onClick={() => onSelect(row.id)}>{row.name || t('expenses.unknownSupplier')}</button></td>
            <td className="p-3 whitespace-nowrap">{format(row.accounting_amount)}</td>
            <td className="p-3">{total > 0 ? `${(Number(row.accounting_amount) / total * 100).toFixed(1)}%` : '—'}</td>
            <td className="p-3">{row.transaction_count}</td>
            <td className="p-3 whitespace-nowrap">{format(row.vat_amount)}</td>
            <td className="p-3 whitespace-nowrap">{format(row.gross_amount)}</td>
            <td className="p-3 whitespace-nowrap">{format(row.previous_amount)}</td>
            <td className="p-3">{row.change_percent == null ? '—' : `${Number(row.change_percent).toFixed(1)}%`}</td>
          </tr>
        ))}</tbody>
      </table>
      {!rows.length && <p className="p-4 text-gray-500">{t('expenses.noMatches')}</p>}
    </div>
  );
}

function Transactions({ filters, snapshot }: { filters: ExpenseFilters; snapshot: string }) {
  const { t } = useTranslation();
  const format = useExpenseFormat();
  const [sort, setSort] = useState<'date' | 'amount'>('date');
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  const [history, setHistory] = useState<(string | undefined)[]>([undefined]);
  const [page, setPage] = useState<ExpenseTransactionPage | null>(null);
  const [error, setError] = useState(false);
  const cursor = history[history.length - 1];
  useEffect(() => {
    const controller = new AbortController();
    setError(false);
    setPage(null);
    getExpenseTransactions({ ...filters, snapshot_id: snapshot, cursor, sort, direction }, controller.signal)
      .then((result) => { if (!controller.signal.aborted) setPage(result); })
      .catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [filters, snapshot, cursor, sort, direction]);
  return (
    <section aria-label={t('expenses.transactions')}>
      <div className="flex flex-wrap gap-3 p-3">
        <label>{t('expenses.sort')} <select className={inputClass} value={sort} onChange={(event) => {
          setSort(event.target.value === 'amount' ? 'amount' : 'date'); setHistory([undefined]);
        }}><option value="date">{t('expenses.date')}</option><option value="amount">{t('expenses.accountingAmount')}</option></select></label>
        <label>{t('expenses.direction')} <select className={inputClass} value={direction} onChange={(event) => {
          setDirection(event.target.value === 'asc' ? 'asc' : 'desc'); setHistory([undefined]);
        }}><option value="desc">{t('expenses.desc')}</option><option value="asc">{t('expenses.asc')}</option></select></label>
      </div>
      {error && <p role="alert" className="p-4 text-red-700">{t('expenses.loadError')}</p>}
      {!page && !error && <p role="status" className="p-4">{t('common.loading')}</p>}
      {page && <div className="overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-gray-50"><tr>
          {['date', 'referenceDate', 'supplier', 'category', 'description', 'accountingAmount', 'vat', 'gross', 'details'].map((key) =>
            <th scope="col" key={key} className="p-3 text-start">{t(`expenses.${key}`)}</th>)}
        </tr></thead><tbody>{page.transactions.map((row) => <tr key={row.id} className="border-t">
          <td className="p-3 whitespace-nowrap">{row.accounting_month}</td>
          <td className="p-3 whitespace-nowrap">{row.reference_date}</td>
          <td className="p-3">{row.supplier_name || t('expenses.unknownSupplier')}</td>
          <td className="p-3">{row.category_name}</td>
          <td className="p-3 max-w-xs">{row.description}{Number(row.accounting_amount) < 0 && <span className="block text-emerald-700">{t('expenses.credit')}</span>}</td>
          <td className="p-3 whitespace-nowrap">{format(row.accounting_amount)}</td>
          <td className="p-3 whitespace-nowrap">{format(row.vat_amount)}</td>
          <td className="p-3 whitespace-nowrap">{format(row.gross_amount)}</td>
          <td className="p-3"><details><summary className="cursor-pointer">{t('expenses.details')}</summary>
            <dl className="min-w-44 space-y-2 py-3">
              <dt>{t('expenses.journal')}</dt><dd dir="ltr">{row.journal_key}</dd>
              <dt>{t('expenses.account')}</dt><dd>{row.account_id}</dd>
              <dt>{t('expenses.references')}</dt><dd>{row.reference1 || '—'} / {row.reference2 || '—'}</dd>
              <dt>{t('expenses.sortCode')}</dt><dd>{row.sort_code ?? '—'}</dd>
              <dt>{t('expenses.currency')}</dt><dd>{row.currency}</dd>
            </dl></details></td>
        </tr>)}</tbody></table>
        {!page.transactions.length && <p className="p-4">{t('expenses.noMatches')}</p>}
        <div className="flex items-center justify-between gap-3 p-4">
          <button className={inputClass} disabled={history.length <= 1} onClick={() => setHistory((value) => value.slice(0, -1))}>{t('common.prev')}</button>
          <span>{t('expenses.rows', { count: page.total })}</span>
          <button className={inputClass} disabled={!page.next_cursor} onClick={() => setHistory((value) => [...value, page.next_cursor || undefined])}>{t('common.next')}</button>
        </div>
      </div>}
    </section>
  );
}

function ExpensesContent() {
  const { t, i18n } = useTranslation();
  const format = useExpenseFormat();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const today = expenseToday();
  const from = params.get('from') || `${today.slice(0, 4)}-01-01`;
  const to = params.get('to') || today;
  const tab = tabs.includes(params.get('view') as Tab) ? params.get('view') as Tab : 'overview';
  const category = params.get('category') || undefined;
  const supplier = params.get('supplier') || undefined;
  const search = params.get('search') || undefined;
  const filters = useMemo(() => ({ from_date: from, to_date: to, category, supplier, search }), [from, to, category, supplier, search]);
  const [cachedReport, setReport] = useState<ExpenseReport | null>(null);
  const [job, setJob] = useState<ExpenseJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [revision, setRevision] = useState(0);
  const attempted = useRef(new Set<string>());
  const loadedScope = useRef('');
  const refreshController = useRef<AbortController | null>(null);
  const tenant = user?.tenant_id;
  const scope = `${tenant}:${from}:${to}`;
  const running = job?.status === 'queued' || job?.status === 'running';
  const jobId = job?.job_id;
  const requestScope = JSON.stringify(filters);
  const report = loadedScope.current === requestScope ? cachedReport : null;
  const update = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => { if (value) next.set(key, value); else next.delete(key); });
    setParams(next);
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    if (loadedScope.current !== requestScope) setReport(null);
    getExpenses(filters, controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      setReport(result);
      loadedScope.current = requestScope;
      if (result.active_job?.status === 'running' || result.active_job?.status === 'queued') setJob(result.active_job);
      else if (result.active_job?.status === 'failed' && result.active_job.from_date === from && result.active_job.to_date === to) {
        setJob(result.active_job);
        setError('expenses.syncError');
      } else if (result.status === 'no_data' && !attempted.current.has(scope)) {
        attempted.current.add(scope);
        void refresh();
      }
    }).catch(() => { if (!controller.signal.aborted) setError('expenses.loadError'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [filters, from, to, scope, revision, requestScope]);

  useEffect(() => {
    if (!jobId || !running) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const result = await getExpenseJob(jobId, controller.signal);
        if (controller.signal.aborted) return;
        if (result.status === 'completed') {
          setJob(result); setRevision((value) => value + 1);
        } else if (result.status === 'failed') {
          setJob(result); setError('expenses.syncError');
        } else {
          setJob(result); timer = setTimeout(poll, 2000);
        }
      } catch {
        if (!controller.signal.aborted) { setJob(null); setError('expenses.pollError'); }
      }
    };
    timer = setTimeout(poll, 2000);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [jobId, tenant, running]);

  useEffect(() => () => refreshController.current?.abort(), [scope]);

  const refresh = async () => {
    const controller = new AbortController();
    refreshController.current?.abort();
    refreshController.current = controller;
    setStarting(true); setError(null);
    try {
      const created = await syncExpenses({ from_date: from, to_date: to }, controller.signal);
      if (controller.signal.aborted) return;
      setJob(created);
      setRevision((value) => value + 1);
    } catch { if (!controller.signal.aborted) setError('expenses.syncError'); }
    finally { if (refreshController.current === controller) setStarting(false); }
  };
  const drill = (changes: Record<string, string | undefined>) => update({ ...changes, view: 'transactions' });
  const months = report?.months.map((month) => ({
    ...month, accounting_amount: Number(month.accounting_amount), previous_amount: Number(month.previous_amount),
    label: month.month + (month.is_partial ? ' *' : ''),
  })) || [];
  const ready = report?.status === 'ready' && report.totals;
  const transactionsKey = JSON.stringify([tenant, filters, report?.snapshot_id]);
  const biggestIncrease = report?.categories.filter((row) =>
    Number(row.previous_amount) > 0 && Number(row.accounting_amount) > Number(row.previous_amount))
    .sort((a, b) => (Number(b.accounting_amount) - Number(b.previous_amount)) - (Number(a.accounting_amount) - Number(a.previous_amount)))[0];
  const insights = [
    { label: 'largestCategory', row: report?.categories.find((row) => Number(row.accounting_amount) > 0), filter: 'category' },
    { label: 'largestSupplier', row: report?.suppliers.find((row) => Number(row.accounting_amount) > 0), filter: 'supplier' },
    { label: 'largestIncrease', row: biggestIncrease, filter: 'category' },
  ];

  return <div className="min-w-0 space-y-6 p-4 md:p-6" dir={i18n.dir()}>
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div><h1 className="text-2xl font-bold">{t('expenses.title')}</h1><p className="mt-1 max-w-3xl text-sm text-gray-500">{t('expenses.basis')}</p></div>
      <button className={`${inputClass} flex items-center gap-2 disabled:opacity-50`} disabled={starting || running || loading || report?.status === 'not_configured' || report?.status === 'unsupported_provider'} onClick={refresh}>
        <RefreshCw size={16} className={running ? 'animate-spin' : ''} />{t('common.refresh')}
      </button>
    </header>
    <div className="flex flex-wrap items-end gap-3 rounded-xl border bg-white p-4">
      <label className="flex flex-col gap-1 text-sm">{t('expenses.from')}<input aria-label={t('expenses.from')} className={inputClass} type="month" value={from.slice(0, 7)} max={to.slice(0, 7)} onChange={(event) => { if (event.target.value) update({ from: expenseMonthRange(event.target.value, today)[0] }); }} /></label>
      <label className="flex flex-col gap-1 text-sm">{t('expenses.to')}<input aria-label={t('expenses.to')} className={inputClass} type="month" value={to.slice(0, 7)} min={from.slice(0, 7)} max={today.slice(0, 7)} onChange={(event) => { if (event.target.value) update({ to: expenseMonthRange(event.target.value, today)[1] }); }} /></label>
      <label className="flex flex-col gap-1 text-sm">{t('expenses.month')}<input className={inputClass} type="month" max={today.slice(0, 7)} onChange={(event) => {
        if (event.target.value) { const [first, last] = expenseMonthRange(event.target.value, today); update({ from: first, to: last }); }
      }} /></label>
      <button className={inputClass} onClick={() => update({ from: `${today.slice(0, 4)}-01-01`, to: today })}>{t('expenses.ytd')}</button>
      <label className="flex min-w-40 flex-col gap-1 text-sm">{t('expenses.category')}<select className={inputClass} value={category || ''} onChange={(event) => update({ category: event.target.value })}>
        <option value="">{t('expenses.allCategories')}</option>{(cachedReport?.category_options || []).map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
      </select></label>
      <label className="flex min-w-40 flex-col gap-1 text-sm">{t('expenses.supplier')}<select className={inputClass} value={supplier || ''} onChange={(event) => update({ supplier: event.target.value })}>
        <option value="">{t('expenses.allSuppliers')}</option>{(cachedReport?.supplier_options || []).map((option) => <option key={option.id} value={option.id}>{option.name || t('expenses.unknownSupplier')}</option>)}
      </select></label>
      <label className="flex flex-col gap-1 text-sm">{t('common.search')}<input className={inputClass} value={search || ''} maxLength={200} onChange={(event) => update({ search: event.target.value })} /></label>
      <button className={inputClass} onClick={() => update({ category: undefined, supplier: undefined, search: undefined })}>{t('expenses.clear')}</button>
    </div>
    {ready && tab === 'overview' && <section className="grid gap-3 md:grid-cols-3" aria-label={t('expenses.insights')}>
      {insights.map(({ label, row, filter }) => row && <button key={label} className="rounded-xl border bg-white p-4 text-start" onClick={() => drill({ [filter]: row.id })}>
        <span className="block text-sm text-gray-500">{t(`expenses.${label}`)}</span>
        <span className="mt-1 block font-semibold">{row.name || t('expenses.unknownSupplier')}</span>
        <span className="block">{format(label === 'largestIncrease' ? Number(row.accounting_amount) - Number(row.previous_amount) : row.accounting_amount)}</span>
      </button>)}
    </section>}
    {error && <div role="alert" className="rounded-lg bg-red-50 p-4 text-red-800">{t(error)} <button className="underline" onClick={() => {
      if (error === 'expenses.syncError') void refresh();
      else { setJob(null); setRevision((value) => value + 1); }
    }}>{t('common.retry')}</button></div>}
    {running && <div role="status" className="rounded-lg bg-blue-50 p-4 text-blue-900">{t('expenses.syncing', { from: job.from_date.slice(0, 7), to: job.to_date.slice(0, 7), completed: job.completed_units, total: job.total_units })}</div>}
    {loading && <p role="status">{t('common.loading')}</p>}
    {report && report.status !== 'ready' && <p className="rounded-xl border bg-white p-6">{t(`expenses.${report.status}`)}</p>}
    {ready && <div className="space-y-6">
      <div className="flex flex-wrap justify-between gap-2 text-sm text-gray-500">
        <span>{t('expenses.lastSync')}: {report.generated_at ? new Date(report.generated_at).toLocaleString(i18n.language) : '—'}</span>
        <span>{t('expenses.comparison', { from: report.previous_from_date.slice(0, 7), to: report.previous_to_date.slice(0, 7) })}</span>
      </div>
      <p className="text-sm text-gray-500">{t('expenses.freshness')}</p>
      {report.warnings.includes('journal_enrichment_unavailable') && <p role="status" className="rounded-lg bg-amber-50 p-4 text-amber-900">{t('expenses.journalWarning')}</p>}
      {report.warnings.includes('vat_incomplete') && <p role="status" className="rounded-lg bg-amber-50 p-4 text-amber-900">{t('expenses.vatWarning')}</p>}
      <nav className="flex flex-wrap gap-2" aria-label={t('expenses.views')}>{tabs.map((value) => <button key={value} aria-current={tab === value ? 'page' : undefined} onClick={() => update({ view: value })}
        className={`rounded-lg px-4 py-2 text-sm ${tab === value ? 'bg-primary-600 text-white' : 'border bg-white'}`}>{t(`expenses.${value}`)}</button>)}</nav>
      {tab === 'overview' && <>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            [t('expenses.accountingAmount'), format(report.totals?.accounting_amount)],
            [t('expenses.vat'), format(report.totals?.vat_amount)],
            [t('expenses.gross'), format(report.totals?.gross_amount)],
            [t('expenses.change'), report.change_percent == null ? '—' : `${Number(report.change_percent).toFixed(1)}%`],
          ].map(([label, value]) => <div key={label} className="rounded-xl border bg-white p-5"><p className="text-sm text-gray-500">{label}</p><p className="mt-2 text-2xl font-bold">{value}</p></div>)}
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="min-w-0 rounded-xl border bg-white p-4"><h2 className="mb-3 font-semibold">{t('expenses.topCategories')}</h2><GroupTable rows={report.categories.slice(0, 5)} grandTotal={report.totals?.accounting_amount} onSelect={(id) => drill({ category: id })} /></section>
          <section className="min-w-0 rounded-xl border bg-white p-4"><h2 className="mb-3 font-semibold">{t('expenses.topSuppliers')}</h2><GroupTable rows={report.suppliers.slice(0, 5)} grandTotal={report.totals?.accounting_amount} onSelect={(id) => drill({ supplier: id })} /></section>
        </div>
      </>}
      {(tab === 'overview' || tab === 'trends') && <section className="rounded-xl border bg-white p-4">
        <h2 className="mb-3 font-semibold">{t('expenses.monthlyTrend')}</h2>
        <div className="h-72" role="img" aria-label={t('expenses.chartDescription')}>
          <ResponsiveContainer width="100%" height="100%"><BarChart data={months}>
            <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="label" /><YAxis /><Tooltip /><Legend />
            <Bar dataKey="accounting_amount" name={t('expenses.current')} fill="#4f46e5" />
            <Bar dataKey="previous_amount" name={t('expenses.previous')} fill="#94a3b8" />
          </BarChart></ResponsiveContainer>
        </div>
        <p className="my-2 text-xs text-gray-500">{t('expenses.partialMonth')}</p>
        <div className="overflow-x-auto"><table className="w-full text-sm"><caption className="sr-only">{t('expenses.monthlyTrend')}</caption><thead><tr>
          {['month', 'current', 'previous'].map((key) => <th scope="col" className="p-2 text-start" key={key}>{t(`expenses.${key}`)}</th>)}
        </tr></thead><tbody>{months.map((month) => <tr className="border-t" key={month.month}><td className="p-2"><button className="underline" onClick={() => {
          const [first, last] = expenseMonthRange(month.month, today);
          drill({ from: first < from ? from : first, to: last > to ? to : last });
        }}>{month.label}</button></td><td className="p-2">{format(month.accounting_amount)}</td><td className="p-2">{format(month.previous_amount)}</td></tr>)}</tbody></table></div>
      </section>}
      {tab === 'trends' && <section className="rounded-xl border bg-white p-4"><h2 className="mb-3 font-semibold">{t('expenses.categories')}</h2><GroupTable rows={report.categories} onSelect={(id) => drill({ category: id })} /></section>}
      {tab === 'trends' && <section className="rounded-xl border bg-white p-4">
        <h2 className="mb-3 font-semibold">{t('expenses.categoryMonths')}</h2>
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr>
          <th scope="col" className="p-2 text-start">{t('expenses.category')}</th>
          {report.months.map((month) => <th scope="col" className="p-2 text-start whitespace-nowrap" key={month.month}>{month.month}{month.is_partial ? ' *' : ''}</th>)}
        </tr></thead><tbody>{report.categories.map((row) => <tr key={row.id} className="border-t">
          <th scope="row" className="p-2 text-start font-normal">{row.name}</th>
          {report.months.map((month) => <td key={month.month} className="p-2 whitespace-nowrap"><button className="underline" onClick={() => {
            const [first, last] = expenseMonthRange(month.month, today);
            drill({ category: row.id, from: first < from ? from : first, to: last > to ? to : last });
          }}>{format(month.categories.find((item) => item.id === row.id)?.accounting_amount ?? 0)}</button></td>)}
        </tr>)}</tbody></table></div>
      </section>}
      {tab === 'suppliers' && <section className="rounded-xl border bg-white p-4"><h2 className="mb-3 font-semibold">{t('expenses.suppliers')}</h2><GroupTable rows={report.suppliers} onSelect={(id) => drill({ supplier: id })} /></section>}
      {tab === 'transactions' && report.snapshot_id && <div className="rounded-xl border bg-white"><Transactions key={transactionsKey} filters={filters} snapshot={report.snapshot_id} /></div>}
    </div>}
  </div>;
}

export default function Expenses() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const periodKey = JSON.stringify([user?.tenant_id, params.get('from'), params.get('to')]);
  return canViewExpenses(user?.role) ? <ExpensesContent key={periodKey} /> : <Navigate to="/home" replace />;
}
